import React, {useEffect, useState, useRef} from 'react';
import {Button} from '@components';
import {Image, ButtonGroup, ProgressBar, Form, Spinner} from 'react-bootstrap';
import {BaseGatewayImage, FILE_MANAGER_API} from '@app/utils/apiUrl.js';
import axios from 'axios';
import {getToken} from '@app/utils/helper';
import {toast} from 'react-toastify';

// uploadFile hàm gửi lên API
const uploadFile = async ({fileUpload, setCompleted}) => {
    const token = await getToken();
    const config = {
        onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
            );
            setCompleted(percentCompleted);
        },
        headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `bearer ${token}`
        }
    };

    const data = new FormData();
    data.append('file', fileUpload);

    try {
        const res = await axios.post(FILE_MANAGER_API.UPLOAD, data, config);
        return res;
    } catch (error) {
        const errorMessage = error.response.data.message;
        return errorMessage;
    }
};

const PreviewFileUpload = ({
    isThumbnail = false,
    fileView,
    uploaded,
    actionRemove,
    isDocument = false
}) => {
    const [completed, setCompleted] = useState(0);
    const [file, setFile] = useState(fileView);
    const inputUpload = useRef();

    const handleUploadFile = async (fileUpload) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            fileUpload.url = e.target.result;
            setFile(fileUpload);
        };
        reader.readAsDataURL(fileUpload);
        const result = await uploadFile({fileUpload, setCompleted});
        // chỗ này khi đổi phần mềm hoặc api dễ sai do đổi sang Text.Json, còn NewstonJson thì ko cần, cần thêm $values
        const fileUpdated = result.data[0];
        fileUpdated.url = BaseGatewayImage + (fileUpdated.thumbPath || fileUpdated.filePath);
        fileUpdated.type = fileUpdated.fileType || '';
        uploaded(fileUpdated);
        setFile(fileUpdated);
        setCompleted(0);
        toast.success(isThumbnail ? 'Tải ảnh thành công' : 'Tải tài liệu thành công');
    };

    useEffect(async () => {
        if (fileView != null && fileView.url == null) {
            handleUploadFile(fileView);
        }
    }, []);

    useEffect(() => {
        if (fileView != null && fileView.url == null && file.url) {
            handleUploadFile(fileView);
        } else {
            setFile(fileView);
        }
    }, [fileView]);

    const handleUpdateFile = async (event) => {
        const fileUpdate = event.target.files[0];
        handleUploadFile(fileUpdate);
    };

    const isUploaded = file.url && completed === 0;
    const showAsImage = file.type?.includes('image') && !isDocument;

    const getImageUrl = (url) => {
        if (!url) return url;
        if (url.startsWith('http') || url.startsWith('blob:') || url.startsWith('data:')) {
            return url;
        }
        return BaseGatewayImage + (url.startsWith('/') ? '' : '/') + url;
    };

    return isThumbnail ? (
        <div className="mgl5 mgt5 mgr5 mgbt5 position-relative">
            <div className="imageThumb">
                <Image
                    src={getImageUrl(file.url)}
                    rounded
                    style={{opacity: isUploaded ? 1 : 0.5}}
                />
                <Spinner
                    animation="border"
                    className="position-absolute"
                    style={{
                        display: isUploaded ? 'none' : 'block',
                        top: '50%',
                        left: '50%',
                        marginLeft: '-15px',
                        marginTop: '-15px'
                    }}
                />
                <i
                    className="fas fa-times mr-2 position-absolute"
                    style={{
                        right: '-2.5px',
                        top: '5px',
                        zIndex: 9,
                        display: !isUploaded ? 'none' : 'block'
                    }}
                    role="presentation"
                    onClick={() => actionRemove()}
                />
            </div>
        </div>
    ) : (
        <>
            <Image
                src={getImageUrl(file.url)}
                thumbnail
                className={`col-sm-2 ${
                    !showAsImage && 'd-none'
                }`}
            />
            {!showAsImage && (
                <div className="d-flex align-items-center pl-2">
                    <i className="fas fa-file-alt text-primary" style={{ fontSize: '1.2rem' }} />
                </div>
            )}

            <div 
                className="col ml-3 d-flex align-items-center" 
                title={file.name}
                style={{ minWidth: 0 }}
            >
                <span className="text-truncate w-100">
                    {file.name}
                </span>
            </div>
            <div className="col-4 d-flex align-items-center">
                <ProgressBar
                    className="progress-striped active w-100"
                    style={{
                        display: isUploaded ? 'none' : 'flex'
                    }}
                    variant="success"
                    animated
                    now={completed}
                />
            </div>
            <div className="col-auto d-flex align-items-center">
                <Form.Control
                    type="file"
                    hidden
                    accept="image/*"
                    ref={inputUpload}
                    onChange={(e) => handleUpdateFile(e)}
                />
                {isDocument ? (
                    <>
                        <a
                            className={`btn btn-sm btn-outline-success mr-2 ${!isUploaded ? 'disabled' : ''}`}
                            href={getImageUrl(file.url)}
                            download
                            target="_blank"
                            title="Tải xuống"
                        >
                            <i className="fas fa-download" />
                        </a>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={actionRemove}
                            disabled={!isUploaded}
                            title="Xóa"
                        >
                            <i className="far fa-trash-alt" />
                        </button>
                    </>
                ) : (
                    <>
                        <a
                            className={`btn btn-sm btn-outline-info mr-2 ${!isUploaded ? 'disabled' : ''} ${!showAsImage && 'd-none'}`}
                            href={getImageUrl(file.url)}
                            target="_blank"
                            title="Xem"
                        >
                            <i className="fas fa-eye" />
                        </a>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-primary mr-2"
                            onClick={() => inputUpload.current.click()}
                            disabled={!isUploaded}
                            title="Cập nhật"
                        >
                            <i className="fas fa-upload" />
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={actionRemove}
                            disabled={!isUploaded}
                            title="Xóa"
                        >
                            <i className="far fa-trash-alt" />
                        </button>
                    </>
                )}
            </div>
        </>
    );
};

export default PreviewFileUpload;
